/**
 * CountryRegion — a country's boundary, lit up and breathing on the globe.
 *
 * Marks a country-level outage (IODA): every ring of the country's polygon is
 * drawn as a glowing outline laid just above the surface, with its opacity
 * pulsing so the whole border breathes. Like GeoLines, each edge is subdivided
 * along the great circle between adjacent vertices — so a country that straddles
 * the antimeridian (Russia, Fiji) curves the short way across the surface
 * instead of slashing a chord through the globe. Render as a child of `<Globe>`.
 *
 * See pipeline/countries.py and docs/BUILD_PLAN.md (P5).
 */

import { useFrame } from '@react-three/fiber';
import { useMemo } from 'react';
import {
  AdditiveBlending,
  BufferGeometry,
  Color,
  Float32BufferAttribute,
  LineBasicMaterial,
  Vector3,
} from 'three';

import { latLonToVec3 } from './coords.js';

/** A boundary ring: an ordered list of `[lon, lat]` points (GeoJSON order). */
export type CountryRing = ReadonlyArray<readonly [number, number]>;

export interface CountryRegionProps {
  readonly rings: ReadonlyArray<CountryRing>;
  /** Radius to lay the outline on. Slightly above the surface. Default 1.004. */
  readonly radius?: number;
  readonly color?: string | number;
  /** Seconds per breath. Default 1.6. */
  readonly period?: number;
}

// Match GeoLines: subdivide edges longer than ~0.06 rad (~3.4°) so they hug the
// surface and take the short great-circle path between vertices.
const MAX_STEP_RAD = 0.06;

function slerpUnit(a: Vector3, b: Vector3, t: number, out: Vector3): Vector3 {
  const dot = Math.min(1, Math.max(-1, a.dot(b)));
  const omega = Math.acos(dot);
  if (omega < 1e-6) return out.copy(a);
  const sinOmega = Math.sin(omega);
  const wa = Math.sin((1 - t) * omega) / sinOmega;
  const wb = Math.sin(t * omega) / sinOmega;
  return out.copy(a).multiplyScalar(wa).addScaledVector(b, wb);
}

export function CountryRegion({
  rings,
  radius = 1.004,
  color = 0xff5a5a,
  period = 1.6,
}: CountryRegionProps): JSX.Element | null {
  const geometry = useMemo(() => {
    const positions: number[] = [];
    const dirA = new Vector3();
    const dirB = new Vector3();
    const prev = new Vector3();
    const cur = new Vector3();

    for (const ring of rings) {
      for (let i = 0; i + 1 < ring.length; i += 1) {
        const a = ring[i];
        const b = ring[i + 1];
        if (!a || !b) continue;
        dirA.copy(latLonToVec3(a[1], a[0], 1));
        dirB.copy(latLonToVec3(b[1], b[0], 1));
        const angle = dirA.angleTo(dirB);
        const steps = Math.max(1, Math.ceil(angle / MAX_STEP_RAD));
        slerpUnit(dirA, dirB, 0, prev).multiplyScalar(radius);
        for (let s = 1; s <= steps; s += 1) {
          slerpUnit(dirA, dirB, s / steps, cur).multiplyScalar(radius);
          positions.push(prev.x, prev.y, prev.z, cur.x, cur.y, cur.z);
          prev.copy(cur);
        }
      }
    }

    const geo = new BufferGeometry();
    geo.setAttribute('position', new Float32BufferAttribute(positions, 3));
    return geo;
  }, [rings, radius]);

  const material = useMemo(
    () =>
      new LineBasicMaterial({
        color: new Color(color),
        transparent: true,
        opacity: 0.6,
        blending: AdditiveBlending,
        depthWrite: false,
      }),
    [color],
  );

  useFrame((state) => {
    // Breathe between ~0.35 and ~0.85 so the whole border pulses.
    const phase = 0.5 + 0.5 * Math.sin((state.clock.elapsedTime / period) * Math.PI * 2);
    material.opacity = 0.35 + 0.5 * phase;
  });

  if (rings.length === 0) return null;
  return <lineSegments geometry={geometry} material={material} renderOrder={-2} />;
}
